import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import {
  FileText, BookOpen, Eye, Check, ChevronLeft, Plus, Trash2,
  Save, Send, Edit2, Lightbulb, Hash, HelpCircle, Home, ArrowRight
} from './components/DeskeyIcons';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Skeleton } from './components/ui/skeleton';
import { Badge } from './components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Draft Editor Component
const DraftEditor = ({ draft, onSave, onPublish, saving, publishing }) => {
  const [editDraft, setEditDraft] = useState(draft);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` }; setEditDraft(draft); }, [draft]);

  const updateField = (field, value) => setEditDraft(d => ({ ...d, [field]: value }));

  const updateConcept = (i, value) => {
    const concepts = [...editDraft.key_concepts];
    concepts[i] = value;
    updateField('key_concepts', concepts);
  };

  const updateNote = (i, value) => {
    const notes = [...editDraft.bullet_notes];
    notes[i] = value;
    updateField('bullet_notes', notes);
  };

  return (
    <div className="lesson-editor space-y-5">
      {/* Title & Chapter */}
      <div className="space-y-3">
        <div>
          <Label className="text-xs font-medium mb-1 block" style={{ color: 'hsl(var(--muted-foreground))' }}>LESSON TITLE</Label>
          <input className="w-full text-xl font-bold border-0 border-b-2 pb-2 bg-transparent focus:outline-none"
            style={{ borderColor: 'hsl(var(--border))', fontFamily: 'Space Grotesk, sans-serif' }}
            value={editDraft.title || ''}
            onChange={e => updateField('title', e.target.value)}
            placeholder="Lesson title"
            data-testid="draft-title-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'hsl(var(--muted-foreground))' }}>CHAPTER</Label>
            <Input value={editDraft.chapter || ''} onChange={e => updateField('chapter', e.target.value)} placeholder="Chapter name" />
          </div>
        </div>
      </div>

      {/* Topic Summary */}
      <div>
        <Label className="text-xs font-medium mb-2 block flex items-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
          <Lightbulb className="w-3.5 h-3.5" /> TOPIC SUMMARY
        </Label>
        <textarea className="w-full rounded-xl p-3 text-sm resize-none focus:outline-none border"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))', minHeight: '80px' }}
          value={editDraft.topic_summary || ''}
          onChange={e => updateField('topic_summary', e.target.value)}
          placeholder="Brief summary of this lesson..."
          data-testid="draft-summary-input" />
      </div>

      {/* Key Concepts */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
            <Hash className="w-3.5 h-3.5" /> KEY CONCEPTS
          </Label>
          <Button variant="ghost" size="sm" className="h-7 text-xs"
            onClick={() => updateField('key_concepts', [...(editDraft.key_concepts || []), ''])}
          ><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {(editDraft.key_concepts || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))' }}>{i + 1}</div>
              <Input value={c} onChange={e => updateConcept(i, e.target.value)} placeholder={`Concept ${i + 1}`} className="flex-1" />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400"
                onClick={() => updateField('key_concepts', editDraft.key_concepts.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Bullet Notes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
            <FileText className="w-3.5 h-3.5" /> BULLET NOTES
          </Label>
          <Button variant="ghost" size="sm" className="h-7 text-xs"
            onClick={() => updateField('bullet_notes', [...(editDraft.bullet_notes || []), ''])}
          ><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
        </div>
        <div className="space-y-2">
          {(editDraft.bullet_notes || []).map((n, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ background: 'hsl(var(--primary))' }} />
              <textarea className="flex-1 rounded-lg p-2 text-sm resize-none border focus:outline-none"
                style={{ borderColor: 'hsl(var(--border))', minHeight: '40px' }}
                value={n} onChange={e => updateNote(i, e.target.value)} placeholder={`Note ${i + 1}`} rows={1} />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 mt-0.5"
                onClick={() => updateField('bullet_notes', editDraft.bullet_notes.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Homework */}
      <div>
        <Label className="text-xs font-medium mb-2 block flex items-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
          <Home className="w-3.5 h-3.5" /> HOMEWORK
        </Label>
        <textarea className="w-full rounded-xl p-3 text-sm resize-none focus:outline-none border"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))', minHeight: '64px' }}
          value={editDraft.homework || ''}
          onChange={e => updateField('homework', e.target.value)}
          placeholder="Homework assignment for students..."
          data-testid="draft-homework-input" />
      </div>

      {/* Teacher Notes */}
      <div>
        <Label className="text-xs font-medium mb-2 block flex items-center gap-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
          <Edit2 className="w-3.5 h-3.5" /> TEACHER NOTES (private)
        </Label>
        <textarea className="w-full rounded-xl p-3 text-sm resize-none focus:outline-none border"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))', minHeight: '64px' }}
          value={editDraft.teacher_notes || ''}
          onChange={e => updateField('teacher_notes', e.target.value)}
          placeholder="Private notes for yourself..."
          data-testid="draft-teacher-notes-input" />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 pt-2">
        <Button onClick={() => onSave(editDraft)} disabled={saving}
          variant="outline" className="flex items-center gap-2"
          data-testid="save-draft-button">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button onClick={() => onPublish(draft.id, editDraft)} disabled={publishing}
          style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}
          className="flex items-center gap-2"
          data-testid="publish-draft-button">
          <Send className="w-4 h-4" />
          {publishing ? 'Publishing...' : 'Publish to Students'}
        </Button>
      </div>
    </div>
  );
};

export default function TeacherDrafts() {
  const { user, token } = useApp();
  const navigate = useNavigate();
  const { draftId } = useParams();
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    const tid = user?.tenant_id;
    axios.get(`${API}/lesson-drafts`, { params: { tenant_id: tid, teacher_id: user?.id } , headers })
      .then(r => {
        setDrafts(r.data);
        if (draftId) {
          const found = r.data.find(d => d.id === draftId);
          if (found) setSelectedDraft(found);
        } else if (r.data.length > 0) {
          setSelectedDraft(r.data[0]);
        }
      })
      .catch(() => toast.error('Failed to load drafts'))
      .finally(() => setLoading(false));
  }, [user?.id, draftId]);

  const saveDraft = async (updatedDraft) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/lesson-drafts/${selectedDraft.id}`, updatedDraft);
      setSelectedDraft(res.data);
      setDrafts(drafts.map(d => d.id === res.data.id ? res.data : d));
      toast.success('Draft saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const publishDraft = async (draftId, updatedDraft) => {
    setPublishing(true);
    try {
      // Save first, then publish
      await axios.put(`${API}/lesson-drafts/${draftId}`, updatedDraft);
      const res = await axios.post(`${API}/lesson-drafts/${draftId}/publish`);
      toast.success('Lesson published to students!');
      // Refresh
      const dr = await axios.get(`${API}/lesson-drafts`, { params: { tenant_id: user.tenant_id, teacher_id: user.id } , headers });
      setDrafts(dr.data);
      const updated = dr.data.find(d => d.id === draftId);
      if (updated) setSelectedDraft(updated);
    } catch { toast.error('Failed to publish'); }
    finally { setPublishing(false); }
  };

  if (loading) return (
    <DashboardShell title="Lesson Drafts">
      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-screen rounded-2xl" />
        <div className="col-span-2 space-y-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      </div>
    </DashboardShell>
  );

  return (
    <DashboardShell title="Lesson Drafts" breadcrumb="Teacher Dashboard">
      <div className="flex gap-4 h-full fade-in">
        {/* Draft List */}
        <div className="w-72 flex-shrink-0">
          <div className="space-y-1 sticky top-4">
            {drafts.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <p className="text-sm">No drafts yet</p>
                <Button size="sm" className="mt-3" onClick={() => navigate('/teacher/recordings')}>Start Recording</Button>
              </div>
            ) : drafts.map(d => (
              <button key={d.id}
                onClick={() => { setSelectedDraft(d); navigate(`/teacher/drafts/${d.id}`); }}
                className={`w-full text-left p-3 rounded-xl border transition-all ${
                  selectedDraft?.id === d.id
                    ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
                    : 'border-transparent hover:bg-[hsl(var(--accent))]'
                }`}
                data-testid={`draft-item-${d.id}`}>
                <p className="text-sm font-medium truncate">{d.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full status-${d.status}`}>{d.status}</span>
                  <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {new Date(d.created_at).toLocaleDateString()}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 min-w-0">
          {selectedDraft ? (
            <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
              <CardContent className="p-6">
                {selectedDraft.is_mock && (
                  <div className="mb-4 p-3 rounded-xl text-sm" style={{ background: 'hsl(36 92% 95%)', color: 'hsl(36 92% 25%)' }}>
                    ⚠️ This draft was generated as a mock (AI unavailable). Please review and edit before publishing.
                  </div>
                )}
                {selectedDraft.confidence_score > 0 && (
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>AI Confidence:</span>
                    <div className="flex-1 max-w-32">
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(var(--muted))' }}>
                        <div className="h-full rounded-full" style={{ width: `${(selectedDraft.confidence_score * 100).toFixed(0)}%`, background: 'hsl(var(--success))' }} />
                      </div>
                    </div>
                    <span className="text-xs font-medium">{(selectedDraft.confidence_score * 100).toFixed(0)}%</span>
                  </div>
                )}
                <DraftEditor
                  draft={selectedDraft}
                  onSave={saveDraft}
                  onPublish={publishDraft}
                  saving={saving}
                  publishing={publishing}
                />
              </CardContent>
            </Card>
          ) : (
            <div className="empty-state h-96">
              <FileText className="empty-state-icon" />
              <p className="text-sm font-medium">Select a draft to edit</p>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
